import axios from "axios";
import { useQuery, useMutation } from "@tanstack/react-query";
import { API_BACKEND_URL } from "../Config/Config";
import { getUserDetails } from "../utils/userDetailsUtils";

//Create an API suite

const createApiSuite = async (suiteName) => {
  const user = getUserDetails();
  const response = await axios.post(
    `${API_BACKEND_URL}/testsuite/addTestSuite?companyid=${user.companyid}`,
    { testSuiteName: suiteName },
  );
  return response.data;
};

export const useCreateApiSuite = (options = {}) => {
  return useMutation({
    mutationFn: createApiSuite,
    ...options,
  });
};

//GET API SUITES

const getApiSuites = async () => {
  const user = getUserDetails();

  const response = await axios.get(
    `${API_BACKEND_URL}/testsuite?companyid=${user.companyid}`,
  );
  return response.data;
};

export const useGetApiSuites = (options = {}) => {
  return useMutation({
    queryKey: ["apisuites"],
    mutationFn: getApiSuites,
    ...options,
  });
};

//Delete an API suite
const deleteApiSuite = async (testSuiteId) => {
  const user = getUserDetails();

  const response = await axios.delete(
    `${API_BACKEND_URL}/testsuite/deleteTestSuite?companyid=${user.companyid}&testSuiteId=${testSuiteId}`,
  );
  return response.data;
};

export const useDeleteApiSuite = (options = {}) => {
  return useMutation({
    mutationFn: deleteApiSuite,
    ...options,
  });
};

//GET COLLECTIONS
const getCollections = async () => {
  const user = getUserDetails();

  const response = await axios.get(
    `${API_BACKEND_URL}/collections?companyid=${user.companyid}`,
  );
  return response.data;
};

export const useGetCollections = (options = {}) => {
  return useMutation({
    queryKey: ["collections"],
    mutationFn: getCollections,
    ...options,
  });
};

//DELETE a collection

const deleteCollection = async ({ collectionId }) => {
  const user = getUserDetails();

  const response = await axios.delete(
    `${API_BACKEND_URL}/collections/deleteCollection?companyid=${user.companyid}&collectionId=${collectionId}`,
  );
  return response.data;
};

export const useDeleteCollection = (options = {}) => {
  return useMutation({
    mutationFn: deleteCollection,
    ...options,
  });
};

//add an endpoint to suite

const addEndpoint = async (data) => {
  const user = getUserDetails();

  const response = await axios.post(
    `${API_BACKEND_URL}/testsuite/addEndpointToTestSuite?companyid=${user.companyid}`,
    data,
  );
  return response.data;
};

export const useAddEndpoint = (options = {}) => {
  return useMutation({
    mutationFn: addEndpoint,
    ...options,
  });
};

// GET TEST CASES BY ENDPOINT
const getTestCasesByEndpoint = async ({ endpointId }) => {
  const user = getUserDetails();

  const response = await axios.get(
    `${API_BACKEND_URL}/testcase/getTestCasesByEndpoint?companyid=${user.companyid}&endpointId=${endpointId}`,
  );
  return response.data;
};

export const useGetTestCasesByEndpoint = (options = {}) => {
  return useMutation({
    mutationFn: getTestCasesByEndpoint,
    ...options,
  });
};

// GET ENDPOINTS FOR TEST SUITE
const getEndpointsForTestSuite = async (testSuiteId) => {
  const user = getUserDetails();

  const response = await axios.get(
    `${API_BACKEND_URL}/testsuite/getEndpointsForTestSuite?companyid=${user.companyid}&testSuiteId=${testSuiteId}`,
  );
  return response.data;
};

export const useGetEndpointsForTestSuite = (testSuiteId, options = {}) => {
  return useQuery({
    queryKey: ["suiteEndpoints", testSuiteId],
    queryFn: () => getEndpointsForTestSuite(testSuiteId),
    enabled: !!testSuiteId,
    ...options,
  });
};
// GET CHAINS
const getChains = async () => {
  const user = getUserDetails();

  const response = await axios.get(
    `${API_BACKEND_URL}/chains?companyid=${user.companyid}`,
  );
  return response.data;
};

export const useGetChains = (options = {}) => {
  return useMutation({
    mutationFn: getChains,
    ...options,
  });
};

//DELETE TEST CASE FROM SUITE

const deleteTestCaseFromSuite = async ({ testCaseId, testSuiteId }) => {
  const user = getUserDetails();

  const response = await axios.delete(
    `${API_BACKEND_URL}/testsuite/deleteTestCaseFromTestSuite?companyid=${user.companyid}&testSuiteId=${testSuiteId}&testCaseId=${testCaseId}`,
  );
  return response.data;
};

export const useDeleteTestCaseFromSuite = (options = {}) => {
  return useMutation({
    mutationFn: deleteTestCaseFromSuite,
    ...options,
  });
};

//DELETE A CHAIN FROM  SUITE

const deleteChainFromSuite = async ({ testSuiteId, chainId }) => {
  const user = getUserDetails();
  const response = await axios.delete(
    `${API_BACKEND_URL}/testsuite/deleteChainFromTestSuite?companyid=${user.companyid}&testSuiteId=${testSuiteId}&chainId=${chainId}`,
  );
  return response.data;
};

export const useDeleteChainFromSuite = (options = {}) => {
  return useMutation({
    mutationFn: deleteChainFromSuite,
    ...options,
  });
};

//RUN API SUITE

const runApiSuite = async ({ testSuiteId }) => {
  const user = getUserDetails();
  const response = await axios.post(
    `${API_BACKEND_URL}/testsuite/runTestSuite?companyId=${user.companyid}&testSuiteId=${testSuiteId}&intNum=${0}`,
  );
  return response.data;
};

export const useRunApiSuite = (options = {}) => {
  return useMutation({
    mutationFn: runApiSuite,
    ...options,
  });
};

//GET SUITE RESULTS LIST

const getApiSuiteResults = async ({ queryKey }) => {
  const [, testSuiteId] = queryKey;
  const user = getUserDetails();

  const response = await axios.get(
    `${API_BACKEND_URL}/testsuiteResults/getResultsForTestSuite?companyid=${user.companyid}&testSuiteId=${testSuiteId}`,
  );
  return response.data;
};

export const useGetApiSuiteResults = (testSuiteId, options = {}) => {
  return useQuery({
    queryKey: ["apiSuiteResults", testSuiteId],
    queryFn: getApiSuiteResults,
    enabled: !!testSuiteId,
    ...options,
  });
};

//GET Individual suite run result

const getSingleSuiteResult = async ({ testSuiteResultId }) => {
  const user = getUserDetails();
  const response = await axios.get(
    `${API_BACKEND_URL}/testsuiteResults/getTestCaseResultsForTestSuite?companyid=${user.companyid}&testSuiteResultId=${testSuiteResultId}`,
  );
  return response.data;
};

export const useGetSingleSuiteResult = (testSuiteResultId, options = {}) => {
  return useQuery({
    queryKey: ["singleSuiteResult", testSuiteResultId],
    queryFn: () => getSingleSuiteResult({ testSuiteResultId }),
    enabled: !!testSuiteResultId,
    ...options,
  });
};

//Update Suite Results Email

const updateEmail = async ({ testSuiteId, email }) => {
  const user = getUserDetails();

  const response = await axios.post(
    `${API_BACKEND_URL}/testsuite/updateEmail?testSuiteId=${testSuiteId}&emailId=${email}&companyid=${user.companyid}`,
    { testSuiteId, email },
  );
  return response.data;
};

export const useUpdateEmail = (options = {}) => {
  return useMutation({
    mutationFn: updateEmail,
    ...options,
  });
};

//Generate test cases for a collection (as whole) or generate test cases for specific endpoints from a collection

const generateTestCasesForCollection = async ({
  collectionId,
  endpointsData,
  numberOfTestCases,
}) => {
  const user = getUserDetails();
  const response = await axios.post(
    `${API_BACKEND_URL}/createTestCases?companyid=${user.companyid}&collectionIds=${collectionId}&numberOfTestCases=${numberOfTestCases || 5}`,
    endpointsData,

    /* 

    if user passes no endpointsData, the endpointsData will be null and test cases will be genreated for all the endpoints in the collection
    , and if he passes endponts data, means he wants to generate
    test cases only for those endpoints from the collection
d":1019, "method": "POST" "path":"/store/order"},
      ]
    */
  );
  return response.data;
};

export const useGenerateTestCasesForCollection = (options = {}) => {
  return useMutation({
    mutationFn: generateTestCasesForCollection,
    ...options,
  });
};

const generateChainedTestCases = async ({ collectionId, endpointIds }) => {
  const user = getUserDetails();

  const requestBody = {};

  if (collectionId) {
    requestBody.collectionIds = collectionId;
  }

  if (endpointIds) {
    requestBody.endpointIds = endpointIds;
  }

  const response = await axios.post(
    `${API_BACKEND_URL}/chains/generateChains?companyId=${user.companyid}`,
    requestBody,
  );

  return response.data;
};

export const useGenerateChainedTestCases = (options = {}) => {
  return useMutation({
    mutationFn: generateChainedTestCases,
    ...options,
  });
};

//Run a collection

const runCollection = async ({ endpointsData, collectionId, environmentId, iterations = 1 }) => {
  const user = getUserDetails();
  const response = await axios.post(
    `${API_BACKEND_URL}/collections/runCollection?companyId=${user.companyid}&collectionId=${collectionId}&iterations=${iterations}&environmentId=${environmentId}`,
    endpointsData,
  );
  return response.data;
};

export const useRunCollection = (options = {}) => {
  return useMutation({
    mutationFn: runCollection,
    ...options,
  });
};
